import {
  doc,
  getDoc,
  setDoc,
  query,
  collection,
  where,
  getDocs,
  orderBy,
  collectionGroup,
  limit,
  Query
} from 'firebase/firestore'
import { Collections, Subcollections } from '../../../pure-js/libs/Collections.js'
import { mapQueryResponse } from '../../../pure-js/libs/FirebaseStorageClientBaseHelper.js'
import { PartialUser, User } from '../../../pure-js/types/User.js'
import { Id } from '../../../pure-js/types/types.js'
import { db } from './Firebase.js'
import {
  Activity,
  ActivityStatus,
  Client,
  ClientHost,
  DropinTicket,
  DropinTicketPublic,
  UrlShortHash,
  PrivateLesson,
  EnrollmentUser
} from '../../../pure-js/types/GrooverTypes.js'
import { useFirestoreQuery } from '../hooks/useFirestoreQuery.js'
import { createUniqueId } from '../../../pure-js/libs/Common.js'
import { StripeConfig, StripePaymentIntentStatus } from '../../../pure-js/types/StripeTypes.js'
import { isCourselySubdomain, extractCourselySubdomain } from '../../../pure-js/libs/SeoHelper.js'
import invariant from 'invariant'
import { useMemo } from 'react'
import { safeAsync } from '../utils/safeAsync.js'

/**
 * Resolves a client ID from a hostname
 * Handles both Coursely subdomains (danceschool.coursely.se) and custom domains (courses.danceschool.se)
 */
export async function resolveClientFromHostname(hostname: string, isMounted?: () => boolean): Promise<string | null> {
  const executeOperation = async (): Promise<string | null> => {
    // Strip port for local development
    const hostnameWithoutPort = hostname.split(':')[0]

    // Check if it's a Coursely subdomain
    if (isCourselySubdomain(hostnameWithoutPort)) {
      const subdomain = extractCourselySubdomain(hostnameWithoutPort)
      if (!subdomain) return null

      const q = query(
        collection(db, Collections.CLIENTS_HOST),
        where('subdomain', '==', subdomain),
        where('enabled', '==', true),
        limit(1)
      )

      // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
      const snapshot = await getDocs(q)
      if (snapshot.empty) return null

      const clientHost = snapshot.docs[0].data() as ClientHost
      return clientHost.clientId || null
    }

    // Otherwise, check custom domains (array-contains)
    const q = query(
      collection(db, Collections.CLIENTS_HOST),
      where('customDomains', 'array-contains', hostnameWithoutPort),
      where('enabled', '==', true),
      limit(1)
    )

    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null

    const clientHost = snapshot.docs[0].data() as ClientHost
    return clientHost.clientId || null
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'resolveClientFromHostname' })
  }

  return executeOperation()
}

export const updateUser = (user: PartialUser): Promise<unknown> =>
  // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
  setDoc(doc(db, `${Collections.USERS}/${user.id}`), user, { merge: true })

// Overloaded function signatures for mount safety
export function getUser(id: Id): Promise<User | undefined>
export function getUser(id: Id, isMounted: () => boolean): Promise<User | null>
export function getUser(id: Id, isMounted?: () => boolean): Promise<User | undefined | null> {
  if (isMounted) {
    return safeAsync(() => getDoc(doc(db, `${Collections.USERS}/${id}`)).then(mapQueryResponse), isMounted, {
      logContext: 'getUser'
    })
  }
  // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
  return getDoc(doc(db, `${Collections.USERS}/${id}`)).then(mapQueryResponse)
}

export const upsertActivity = (o: Activity): Promise<unknown> =>
  // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
  setDoc(doc(db, `${Collections.ACTIVITIES}/${o.id}`), o, { merge: true })

// Overloaded function signatures for mount safety
export function getActivity(id: Id): Promise<Activity | undefined>
export function getActivity(id: Id, isMounted: () => boolean): Promise<Activity | null>
export function getActivity(id: Id, isMounted: () => boolean, clientId: string): Promise<Activity | null>
export async function getActivity(
  id: Id,
  isMounted?: () => boolean,
  clientId?: string
): Promise<Activity | undefined | null> {
  const executeOperation = async (): Promise<Activity | undefined> => {
    try {
      // SEO lookups pass clientId + a slug. A getDoc(activities/<slug>) can never hit, and on Safari
      // that guaranteed miss has been measured at ~30s (WebChannel stall) before returning exists:false.
      if (!clientId) {
        const docRef = doc(db, `${Collections.ACTIVITIES}/${id}`)
        // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          const mapped = mapQueryResponse(docSnap)

          if (mapped?.status === ActivityStatus.Deleted) {
            return undefined
          }

          return mapped
        }
      }

      // If not found by ID, try to look up by seoLink
      // If clientId is provided, scope the search to that client (for unique seoLinks per client)
      console.log('🔍 Activity not found by ID, trying seoLink lookup...')

      let seoLinkQuery: Query
      if (clientId) {
        // Client-scoped seoLink lookup (for /{schoolSlug}/activity/{activitySlug} URLs)
        seoLinkQuery = query(
          collection(db, Collections.ACTIVITIES),
          where('clientId', '==', clientId),
          where('seoLink', '==', id)
        )
      } else {
        // Global seoLink lookup (fallback for /activity/{activityId} URLs)
        seoLinkQuery = query(collection(db, Collections.ACTIVITIES), where('seoLink', '==', id))
      }

      // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
      const seoLinkSnapshot = await getDocs(seoLinkQuery)

      if (!seoLinkSnapshot.empty) {
        // Iterate to find first non-deleted document; multiple docs can share
        // the same seoLink when a corrupt write created an orphaned duplicate.
        for (const activityDoc of seoLinkSnapshot.docs) {
          const mapped = mapQueryResponse(activityDoc)
          if (mapped?.status !== ActivityStatus.Deleted) {
            console.log(`✅ Activity found by seoLink: ${mapped?.id}`)
            return mapped
          }
        }

        return undefined
      }

      return undefined
    } catch (error) {
      console.error('❌ getActivity: Error occurred:', error)
      throw error
    }
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getActivity' })
  }

  return executeOperation()
}

export const upsertClient = (o: Client): Promise<unknown> =>
  // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
  setDoc(doc(db, `${Collections.CLIENTS}/${o.id}`), o, { merge: true })

const clientByIdCache = new Map<string, Client>()

const rememberClient = (client: Client | undefined): Client | undefined => {
  if (client?.id) {
    clientByIdCache.set(client.id, client)
  }
  return client
}

// Overloaded function signatures for mount safety
export function getClient(id: Id): Promise<Client | undefined>
export function getClient(id: Id, isMounted: () => boolean): Promise<Client | null>
export async function getClient(id: Id, isMounted?: () => boolean): Promise<Client | undefined | null> {
  const executeOperation = async (): Promise<Client | undefined> => {
    try {
      const cached = clientByIdCache.get(id)
      if (cached) {
        return cached
      }

      const docRef = doc(db, `${Collections.CLIENTS}/${id}`)
      // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return undefined
      }

      return rememberClient(mapQueryResponse(docSnap))
    } catch (error) {
      console.error('❌ getClient: Error occurred:', error)
      throw error
    }
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getClient' })
  }

  return executeOperation()
}

/**
 * Get a client by its URL-friendly slug
 * Used for SEO-friendly URLs like /{clientSlug}/activity/{activitySlug}
 */
export function getClientBySlug(slug: string): Promise<Client | undefined>
export function getClientBySlug(slug: string, isMounted: () => boolean): Promise<Client | null>
export async function getClientBySlug(slug: string, isMounted?: () => boolean): Promise<Client | undefined | null> {
  const executeOperation = async (): Promise<Client | undefined> => {
    try {
      const q = query(collection(db, Collections.CLIENTS), where('slug', '==', slug), limit(1))

      // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
      const snapshot = await getDocs(q)

      if (snapshot.empty) {
        return undefined
      }

      const clientDoc = snapshot.docs[0]
      return rememberClient(mapQueryResponse(clientDoc))
    } catch (error) {
      console.error('❌ getClientBySlug: Error occurred:', error)
      throw error
    }
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getClientBySlug' })
  }

  return executeOperation()
}

// Overloaded function signatures for mount safety
export function getStripeConfig(client: Client): Promise<StripeConfig>
export function getStripeConfig(client: Client, isMounted: () => boolean): Promise<StripeConfig | null>
export async function getStripeConfig(client: Client, isMounted?: () => boolean): Promise<StripeConfig | null> {
  invariant(client.stripe, `getStripeConfig: Client ${client.id} is missing client.stripe config`)

  const executeOperation = () =>
    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    getDoc(
      doc(db, `${Collections.STRIPE_CONFIG}/${client.stripe!.operator}/frontend/${client.stripe!.environment}`)
    ).then(mapQueryResponse)

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getStripeConfig' })
  }

  return executeOperation()
}

// Overloaded function signatures for mount safety
export function getActivitiesByCustomerId(clientId: string): Promise<Activity[]>
export function getActivitiesByCustomerId(clientId: string, isMounted: () => boolean): Promise<Activity[] | null>
export async function getActivitiesByCustomerId(
  clientId: string,
  isMounted?: () => boolean
): Promise<Activity[] | null> {
  const executeOperation = async (): Promise<Activity[]> => {
    const q = query(collection(db, Collections.ACTIVITIES), where('clientId', '==', clientId))
    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs
      .map((doc) => doc.data() as Activity)
      .filter((activity) => activity.status !== ActivityStatus.Deleted)
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getActivitiesByCustomerId' })
  }

  return executeOperation()
}

export const useSwishPaymentIntentStatus = (instructionUUID: string) =>
  useFirestoreQuery(
    // createUniqueId() is needed here to make query working with no instructionUUID
    doc(db, Collections.SWISH_PAYMENT_INTENT_STATUS, instructionUUID || createUniqueId()),
    {
      fnName: 'getSwishPaymentIntentStatus',
      enabled: !!instructionUUID // run query only when instructionUUID is available
    }
  )

export const useStripePaymentIntentStatus = (paymentIntentId: string | null) =>
  useFirestoreQuery<StripePaymentIntentStatus>(
    doc(db, Collections.STRIPE_PAYMENT_INTENT_STATUS, paymentIntentId || createUniqueId()),
    {
      fnName: 'useStripePaymentIntentStatus',
      enabled: !!paymentIntentId
    }
  )

export const useVippsPaymentStatus = (reference: string) =>
  useFirestoreQuery(
    // createUniqueId() is needed here to make query working with no instructionUUID
    doc(db, Collections.VIPPS_PAYMENT_STATUS, reference || createUniqueId()),
    {
      fnName: 'useVippsPaymentStatus',
      enabled: !!reference // run query only when reference is available
    }
  )

export const useEnrollmentPublic = (enrollmentId: string) =>
  useFirestoreQuery(
    // createUniqueId() is needed here to make query working with no enrollmentId
    doc(db, Collections.ENROLLMENTS_PUBLIC, enrollmentId || `dummy-${createUniqueId()}`),
    {
      fnName: 'useEnrollmentPublic',
      enabled: !!enrollmentId // run query only when enrollmentId is available
    }
  )

// Overloaded function signatures for mount safety
export function getClientActivities(currentActivityClientId: string | undefined): Promise<Activity[]>
export function getClientActivities(
  currentActivityClientId: string | undefined,
  isMounted: () => boolean
): Promise<Activity[] | null>
export async function getClientActivities(
  currentActivityClientId: string | undefined,
  isMounted?: () => boolean
): Promise<Activity[] | null> {
  const executeOperation = async (): Promise<Activity[]> => {
    if (!currentActivityClientId) return []

    // Use proper Firestore query with where clauses for server-side filtering
    const q = query(
      collection(db, Collections.ACTIVITIES),
      where('clientId', '==', currentActivityClientId),
      where('status', 'in', ['active', 'draft'])
    )

    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    const querySnapshot = await getDocs(q)
    const activities = querySnapshot.docs.map((doc) => doc.data() as Activity)

    // Only filter subeventParentId client-side since it's complex logic that can't be done server-side
    return activities.filter(
      (activity) => activity.status === 'active' || (activity.status === 'draft' && activity.subeventParentId)
    )
  }

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getClientActivities' })
  }

  return executeOperation()
}

// Overloaded function signatures for mount safety
export function getUrlShort(hash: string): Promise<UrlShortHash>
export function getUrlShort(hash: string, isMounted: () => boolean): Promise<UrlShortHash | null>
export function getUrlShort(hash: string, isMounted?: () => boolean): Promise<UrlShortHash | null> {
  const executeOperation = () =>
    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    getDoc(doc(db, `${Collections.URL_SHORT}/${hash}`)).then(mapQueryResponse)

  if (isMounted) {
    return safeAsync(executeOperation, isMounted, { logContext: 'getUrlShort' })
  }

  return executeOperation()
}

export const useEarlyBirdBookingsForActivity = (activity: Activity | undefined) =>
  useFirestoreQuery(collection(db, `${Collections.EARLY_BIRD_BOOKINGS}/${activity?.id}/${Subcollections.BOOKINGS}`), {
    fnName: 'useEarlyBirdBookingsForActivity',
    enabled: !!activity?.id && activity?.type === 'Event'
  })

export const useDropinTicketsForUser = (enrollmentUserId: string | null) => {
  const query = useMemo(() => {
    if (!enrollmentUserId) {
      // HACK: read from any public data (which don't exists) to avoid getting permission error
      // return doc(db, Collections.ENROLLMENTS_PUBLIC, `dummy-${createUniqueId()}`)
      return collection(db, `${Collections.ENROLLMENT_USERS}/dummy/${Subcollections.DROPIN_TICKETS}`)
    }
    const path = `${Collections.ENROLLMENT_USERS}/${enrollmentUserId}/${Subcollections.DROPIN_TICKETS}`
    return collection(db, path)
  }, [enrollmentUserId])

  return useFirestoreQuery<DropinTicket[]>(query, {
    fnName: 'useDropinTicketsForUser',
    enabled: !!enrollmentUserId // run query only when ID is available
  })
}

export const useDropinTicketPublic = (dropinTicketId: string | null) => {
  const query = useMemo(() => {
    if (!dropinTicketId) {
      return doc(db, `${Collections.DROPIN_TICKETS_PUBLIC}/dummy`)
    }
    return doc(db, `${Collections.DROPIN_TICKETS_PUBLIC}/${dropinTicketId}`)
  }, [dropinTicketId])

  return useFirestoreQuery<DropinTicketPublic>(query, {
    fnName: 'useDropinTicket',
    enabled: !!dropinTicketId
  })
}

// Portal-specific database functions with mount safety

/**
 * Get private lessons for a specific enrollment user
 * @deprecated Use usePrivateLessonsForUser hook instead for reactive updates
 * @param enrollmentUserId - The enrollment user ID
 * @param isMounted - Function to check if component is still mounted
 * @returns Promise<PrivateLesson[] | null>
 */
export const getPrivateLessonsForUser = async (
  enrollmentUserId: string,
  isMounted: () => boolean
): Promise<PrivateLesson[] | null> => {
  const result = await safeAsync(
    () => {
      // Use collection group query to search across all private_lessons subcollections
      // This matches the Firestore rule at line 154-162 which allows reading from collection group
      const q = query(
        collectionGroup(db, Subcollections.PRIVATE_LESSONS),
        where('enrollmentUserId', '==', enrollmentUserId),
        orderBy('startDate', 'desc')
      )

      return getDocs(q)
    },
    isMounted,
    { logContext: 'getPrivateLessonsForUser' }
  )

  if (!result) {
    return null // Component unmounted
  }

  return result.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data()
      }) as PrivateLesson
  )
}
